import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import './styles.css';
import './i18n';
import { App } from './App.tsx';
import { installChunkReloadHandler } from './app/chunkReload';
import { registerServiceWorker } from './features/pwa/registerServiceWorker';
import { installStaleBuildReload } from './features/pwa/staleBuild';
import { initTheme } from './features/theme/theme';

// Slice 23 — start the live `auto` follow app-wide. First paint was already set
// by the inline pre-paint script in index.html; this only wires the OS-preference
// listener so an `auto` user's app follows dusk without a reload. Idempotent.
initTheme();

// One of two independent stale-chunk recovery mechanisms: Vite's
// `vite:preloadError` window event. The other is a rejected `import()` reaching
// the route error boundary. Both share one guarded, one-shot reload path.
installChunkReloadHandler();

// PWA service worker (prod only) — app-shell cache, offline shell, and the
// foreign-cache eviction that keeps a straggler from carrying traccar-web's
// precache. See features/pwa/registerServiceWorker.ts and public/sw.js.
registerServiceWorker();
installStaleBuildReload();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Bandwidth here is expensive and often metered. Live data arrives over
      // the WebSocket, so polling on window focus is pure waste.
      refetchOnWindowFocus: false,
      staleTime: 30_000,
      retry: 2,
    },
  },
});

const root = document.getElementById('root');
if (!root) throw new Error('#root missing from index.html');

createRoot(root).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </StrictMode>,
);
